import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import dayjs from 'dayjs';

/** Error key the validator below sets on the form group. */
export const DATERANGE_INVERTED_ERROR_KEY = 'daterangeInverted';

const TIME_PATTERN = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;

/**
 * Group validator that rejects a date range whose end lies before its start.
 *
 * A task stored that way is not merely odd: the Montageplan calendar derives its lane layout from
 * `end - start`, so a single inverted row breaks the lane assignment and the row height of a whole team
 * row and its card gets drawn in the neighbouring team's row. The backend rejects such payloads — this
 * keeps the user from running into that error in the first place and disables the save button instead.
 *
 * `getTimes` is for forms that keep the time of day outside the form group (a separate time input whose
 * value is merged into the dates only on submit): the times are then folded in before comparing, so an
 * end time before the start time on the same day is caught too. Call `updateValueAndValidity()` on the
 * group when those times change — the validator does not observe them.
 */
export function daterangeNotInvertedValidator(
  startControlName: string,
  endControlName: string,
  getTimes?: () => { startTime?: string | null; endTime?: string | null },
): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const startValue = group.get(startControlName)?.value;
    const endValue = group.get(endControlName)?.value;
    if (!startValue || !endValue) {
      return null;
    }
    const times = getTimes ? getTimes() : undefined;
    const start = applyTime(startValue, times?.startTime);
    const end = applyTime(endValue, times?.endTime);
    if (!start.isValid() || !end.isValid()) {
      return null;
    }
    return end.isBefore(start) ? { [DATERANGE_INVERTED_ERROR_KEY]: true } : null;
  };
}

/** The date with `HH:mm` applied, or the untouched date when no usable time was handed in. */
function applyTime(date: string | Date, time: string | null | undefined): dayjs.Dayjs {
  const dateValue = dayjs(date);
  if (!time || !TIME_PATTERN.test(time)) {
    return dateValue;
  }
  const [hours, minutes] = time.split(':').map((timePart) => parseInt(timePart, 10));
  return dateValue.hour(hours).minute(minutes).second(0).millisecond(0);
}
